/**
 * Vertex AI Configuration Dialog
 *
 * Settings page dialog for managing Vertex AI / Gemini connections.
 */

import { useState, type ReactNode } from "react";
import {
  Plus,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { VertexConnectionsList } from "./VertexConnectionsList";
import { VertexSetupInstructions } from "./VertexSetupInstructions";
import { useCreateVertexConnection } from "@/hooks/useVertex";
import type { VertexConnectionCreate } from "@/types/vertex";

const GCP_REGIONS = [
  "us-central1",
  "us-east1",
  "us-east4",
  "us-west1",
  "us-west4",
  "europe-west1",
  "europe-west2",
  "europe-west4",
  "asia-east1",
  "asia-northeast1",
  "asia-southeast1",
  "australia-southeast1",
];

const FORM_DEFAULTS: VertexConnectionCreate = {
  connection_name: "",
  gcp_project_id: "",
  gcp_region: "us-central1",
  bigquery_dataset: "",
  service_account_key: "",
};

interface VertexConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function VertexConfigDialog({
  children,
  open,
  onOpenChange,
}: VertexConfigDialogProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [useAdc, setUseAdc] = useState(false);
  const [formData, setFormData] = useState<VertexConnectionCreate>(FORM_DEFAULTS);

  const createMutation = useCreateVertexConnection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: VertexConnectionCreate = {
      connection_name: formData.connection_name,
      gcp_project_id: formData.gcp_project_id,
      gcp_region: formData.gcp_region,
      bigquery_dataset: formData.bigquery_dataset,
    };
    if (useAdc) {
      payload.use_adc = true;
    } else {
      payload.service_account_key = formData.service_account_key;
    }
    try {
      await createMutation.mutateAsync(payload);
      setShowAddForm(false);
      setUseAdc(false);
      setFormData(FORM_DEFAULTS);
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gemini Enterprise Agent Platform</DialogTitle>
        </DialogHeader>

        <VertexConnectionsList />

        {!showAddForm ? (
          <Button
            variant="outline"
            onClick={() => setShowAddForm(true)}
            className="w-full mt-4"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Connection
          </Button>
        ) : (
          <div className="space-y-6 mt-4">
            <Separator />

            <div className="flex items-center justify-between">
              <h4 className="font-medium">New Connection</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInstructions(!showInstructions)}
              >
                {showInstructions ? "Hide" : "Show"} Setup Guide
              </Button>
            </div>

            {showInstructions && (
              <VertexSetupInstructions isLoading={false} />
            )}

            <Separator />

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vertex-dialog-name">Connection Name</Label>
                <Input
                  id="vertex-dialog-name"
                  placeholder="e.g. prod-vertex"
                  value={formData.connection_name}
                  onChange={(e) =>
                    setFormData({ ...formData, connection_name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vertex-dialog-project">GCP Project ID</Label>
                  <Input
                    id="vertex-dialog-project"
                    placeholder="e.g. my-gcp-project"
                    value={formData.gcp_project_id}
                    onChange={(e) =>
                      setFormData({ ...formData, gcp_project_id: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vertex-dialog-region">GCP Region</Label>
                  <Select
                    value={formData.gcp_region}
                    onValueChange={(value) =>
                      setFormData({ ...formData, gcp_region: value })
                    }
                  >
                    <SelectTrigger id="vertex-dialog-region">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      {GCP_REGIONS.map((region) => (
                        <SelectItem key={region} value={region}>
                          {region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vertex-dialog-dataset">BigQuery Dataset</Label>
                <Input
                  id="vertex-dialog-dataset"
                  placeholder="e.g. observability_genai_spans"
                  value={formData.bigquery_dataset}
                  onChange={(e) =>
                    setFormData({ ...formData, bigquery_dataset: e.target.value })
                  }
                  required
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="vertex-dialog-adc">Use Application Default Credentials</Label>
                  <p className="text-xs text-muted-foreground">
                    Use the platform's service account instead of a separate key.
                  </p>
                </div>
                <Switch
                  id="vertex-dialog-adc"
                  checked={useAdc}
                  onCheckedChange={setUseAdc}
                />
              </div>

              {!useAdc && (
                <div className="space-y-2">
                  <Label htmlFor="vertex-dialog-key">Service Account Key (JSON)</Label>
                  <Textarea
                    id="vertex-dialog-key"
                    placeholder='{"type": "service_account", ...}'
                    value={formData.service_account_key}
                    onChange={(e) =>
                      setFormData({ ...formData, service_account_key: e.target.value })
                    }
                    required
                    rows={6}
                    className="font-mono text-xs"
                  />
                </div>
              )}

              {createMutation.isError && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {createMutation.error instanceof Error
                      ? createMutation.error.message
                      : "Failed to create connection"}
                  </span>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    "Create Connection"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
